import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getDayOffStats } from '../../../services'
import { DayOffStats } from '../../../types/response'
import { QueryParams } from '@/types/shared'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import ApprovedIcons from '@/icons/ApprovedIcons'
import PendingIcons from '@/icons/PendingIcons'
import RejectedIcons from '@/icons/RejectedIcons'
import CancelIcons from '@/icons/CancelIcons'
import FileIcons from '@/icons/FileIcons'
import CheckCircleIcons from '@/icons/CheckCircleIcons'

interface StatsHRMProps {
    queryParams: QueryParams
}

interface StatItem {
    key: keyof DayOffStats
    daysKey: keyof DayOffStats
    label: string
    icon: React.ReactNode
}

const StatsHRM = ({ queryParams }: StatsHRMProps) => {
    const { t } = useTranslation()

    const { data, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.DAY_OFF_STATS, queryParams],
        queryFn: () => getDayOffStats(queryParams),
        placeholderData: true,
    })

    const stats = data?.result
    const isInitialLoading = !data && isFetching

    // Format number with comma separator
    const formatNumber = (num: number | undefined | null): string => {
        if (num === undefined || num === null) return '0'
        return num.toLocaleString('en-US')
    }

    const statItems: StatItem[] = [
        {
            key: 'pending_count',
            daysKey: 'pending_days',
            label: t('pending'),
            icon: <PendingIcons className="w-5 h-5 text-gray-500" />,
        },
        {
            key: 'leader_approved_count',
            daysKey: 'leader_approved_days',
            label: t('leader_approved'),
            icon: <CheckCircleIcons className="w-5 h-5 text-gray-500" />,
        },
        {
            key: 'approved_count',
            daysKey: 'approved_days',
            label: t('approved'),
            icon: <ApprovedIcons className="w-5 h-5 text-gray-500" />,
        },
        {
            key: 'rejected_count',
            daysKey: 'rejected_days',
            label: t('rejected'),
            icon: <RejectedIcons className="w-5 h-5 text-gray-500" />,
        },
        {
            key: 'cancelled_count',
            daysKey: 'cancelled_days',
            label: t('cancelled'),
            icon: <CancelIcons className="w-5 h-5 text-gray-500" />,
        },
    ]

    // Only show skeleton on initial load
    if (isInitialLoading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
                {[...Array(7)].map((_, i) => (
                    <Skeleton key={i} className="h-[72px] rounded-md" />
                ))}
            </div>
        )
    }

    return (
        <div
            className={`grid grid-cols-1 md:grid-cols-7 gap-2 transition-opacity ${isFetching ? 'opacity-60' : 'opacity-100'}`}
        >
            {/* Total Requests */}
            <div className="p-3 bg-white rounded-md border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                    <FileIcons className="w-5 h-5 text-gray-500" />
                    <span className="text-xs text-gray-600">
                        {t('total_requests')}
                    </span>
                </div>
                <div className="text-xl font-semibold text-gray-900">
                    {formatNumber(stats?.total_requests)}
                </div>
            </div>

            {/* Total Days */}
            <div className="p-3 bg-white rounded-md border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                    <ApprovedIcons className="w-5 h-5 text-gray-500" />
                    <span className="text-xs text-gray-600">
                        {t('total_days')}
                    </span>
                </div>
                <div className="text-xl font-semibold text-gray-900">
                    {formatNumber(stats?.total_days)}
                </div>
            </div>

            {/* Status Stats */}
            {statItems.map((item) => (
                <div
                    key={item.key}
                    className="p-3 bg-white rounded-md border border-gray-200"
                >
                    <div className="flex items-center gap-2 mb-1">
                        {item.icon}
                        <span className="text-xs text-gray-600">
                            {item.label}
                        </span>
                    </div>
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-xl font-semibold text-gray-900">
                            {formatNumber(stats?.[item.key])}
                        </span>
                        <span className="text-xs text-gray-400">
                            ({formatNumber(stats?.[item.daysKey])} {t('days')})
                        </span>
                    </div>
                </div>
            ))}
        </div>
    )
}

export default StatsHRM
